import React from "react";
import { Badge, UncontrolledTooltip } from "reactstrap";
import CustomSelect from "components/customs/dropdown";
import {
  ACCOUNT_HOUSE_SPEAKER,
  agencyWithSpecialForms,
  canEditRoles,
  congRoles,
} from "utility/constants";
import { regionSelectStyles, statusSelectStyles } from "constants/style";
import {
  ALLOWED_STATUS_TO_BUMP_ALLOCATION,
  ALLOCATION_STATUS_OPTIONS,
  ALLOCATION_STATUS,
} from "constants/status";
import { getFormattedDateString, hasPermission } from "utility/helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentDollar } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import {
  AddCommentBtn,
  CommentsContainer,
  StyledEditIcon,
  StyledEyeIcon,
  StyledTrashIcon,
  ViewCommentBtn,
} from "assets/js/Styles";
import { EDIT_ACTION, VIEW_ACTION } from "constants/actions";
import {
  canEditRow,
  cannotEditStatus,
  getStatusOptions,
  isSpeakerViewOthersTab,
} from "./utility";

const StyledCustomSelect = styled(CustomSelect)`
  width: 100%;

  .select__control:has(.pending-status) {
    background-color: #00316d;
    border: none;
    .pending-status {
      color: white;
    }
  }

  .select__control:has(.requested-status) {
    background-color: #f3c61c;
    border: none;
    .requested-status {
      color: black;
    }
  }

  .select__control:has(.processed-status) {
    background-color: #265ea3;
    border: none;
    .processed-status {
      color: white;
    }
  }

  .select__control:has(.in-progress-status) {
    background-color: #ffbd14;
    border: none;
    .in-progress-status {
      color: black;
    }
  }

  .select__control:has(.approved-status) {
    background-color: #53a24c;
    border: none;
    .approved-status {
      color: white;
    }
  }

  .select__control:has(.disapproved-status) {
    background-color: #df3348;
    border: none;
    .disapproved-status {
      color: white;
    }
  }

  .select__control:has(.downloaded-status) {
    background-color: #f26e01;
    border: none;
    .downloaded-status {
      color: white;
    }
  }

  .select__control:has(.resubmission-status) {
    background-color: #ff4433;
    border: none;
    .resubmission-status {
      color: white;
    }
  }
`;

const StyledBadge = styled(Badge)`
  background: ${(props) =>
    props.status === ALLOCATION_STATUS.PENDING
      ? "#4252c9"
      : "#f3c61c"} !important;

  width: 9px !important;
  height: 12px !important;

  div {
    opacity: 0;
  }
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.iconBlue};
  margin-right: 8px;
`;

const isPendingOrRequested = (status) =>
  status === ALLOCATION_STATUS.PENDING ||
  status === ALLOCATION_STATUS.REQUESTED;

const fundSourceColumn = {
  name: "Fund Source",
  selector: (row) => row?.fundSource,
  identifier: "fundSource",
  sortable: true,
  minWidth: "150px",
  cell: ({ status, fundSource }) =>
    fundSource ? (
      <div
        className={`${isPendingOrRequested(status) ? "font-weight-bold" : ""}`}
      >
        {fundSource}
      </div>
    ) : (
      <div
        className={`${isPendingOrRequested(status) ? "font-weight-bold" : ""}`}
      >
        {"-"}
      </div>
    ),
};

const editorNotesColumn = {
  name: "Editor Notes",
  selector: (row) => row?.editorNotes,
  identifier: "editorNotes",
  sortable: true,
  minWidth: "150px",
  cell: ({ status, editorNotes }) =>
    editorNotes ? (
      <div
        className={`${isPendingOrRequested(status) ? "font-weight-bold" : ""}`}
      >
        {editorNotes?.split(";")?.join(", ") || ""}
      </div>
    ) : (
      <div
        className={`${isPendingOrRequested(status) ? "font-weight-bold" : ""}`}
      >
        {"-"}
      </div>
    ),
};

const nameColumn = {
  name: "Name",
  selector: (row) => row?.congressmanName || row?.name,
  identifier: "congressmanName",
  sortable: true,
  minWidth: "200px",
  cell: ({ status, congressmanName, name }) => {
    const displayName = congressmanName || name;

    return displayName ? (
      <div
        className={`${isPendingOrRequested(status) ? "font-weight-bold" : ""}`}
      >
        {displayName}
      </div>
    ) : (
      <div
        className={`${isPendingOrRequested(status) ? "font-weight-bold" : ""}`}
      >
        {"-"}
      </div>
    );
  },
};

export const columnTable = ({
  id,
  congID,
  tableOption,
  setTableOption,
  selectedAgency,
  role,
  handleStatusChange,
  regions,
  toggleModal,
  setEditingCongressman,
  fetchRecipientMsgs,
  congDetails,
  timer,
  handleBumpIconOnClicked,
  isRoleCongressman,
  handleDeleteAction,
  navigate,
  activeEndorsementTab,
}) => {
  const columns = [
    {
      name: "",
      selector: null,
      sortable: false,
      id: "highlight",
      width: "5px",
      cell: () => null,
    },
    {
      name: "",
      selector: (row) => row?.badge,
      sortable: false,
      width: "20px",
      id: "badge",
      cell: ({ status }, index) => {
        if (isPendingOrRequested(status) && role !== ACCOUNT_HOUSE_SPEAKER) {
          return (
            <StyledBadge
              status={status}
              pill
            >
              <div>{index}</div>
            </StyledBadge>
          );
        }

        return null;
      },
    },
    {
      name: "ID",
      selector: (row) => row?.id,
      identifier: "id",
      sortable: true,
      minWidth: "max-content",
      center: true,
      style: { paddingLeft: 0, paddingRight: 0 },
      cell: (row) => (
        <div
          className={`${
            isPendingOrRequested(row.status) ? "font-weight-bold" : ""
          }`}
        >
          {row.id}
        </div>
      ),
    },
    {
      name: "Program",
      selector: (row) => row?.project,
      identifier: "project",
      grow: 2,
      sortable: true,
      minWidth: "180px",
      cell: (row) => (
        <div
          className={`${
            isPendingOrRequested(row.status) ? "font-weight-bold" : ""
          }`}
        >
          {row.project}
        </div>
      ),
    },
    {
      name: "Recommended Amount",
      selector: (row) => row?.budget_allocation,
      identifier: "budget_allocation",
      grow: 2.1,
      sortable: true,
      minWidth: "150px",
      cell: (row) => (
        <div
          className={`${
            isPendingOrRequested(row.status) ? "font-weight-bold" : ""
          }`}
        >
          {`PHP ${Number(row.budget_allocation)?.toLocaleString()}`}
        </div>
      ),
    },
    {
      name: "Type",
      selector: (row) => row?.allocationType,
      identifier: "allocationType",
      sortable: true,
      minWidth: "100px",
      cell: ({ status, allocationType }) => {
        return (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {allocationType?.name}
          </div>
        );
      },
    },
    {
      name: "Other Info",
      selector: (row) => row?.allocationType,
      identifier: "allocationType",
      sortable: true,
      minWidth: "170px",
      grow: 1,
      cell: ({
        status,
        allocationType,
        allocationName,
        allocationContactName,
        institution,
        otherInfo,
      }) => {
        let renderOtherInfo = null;

        if (
          Number(selectedAgency?.ID) === 3 &&
          (allocationType.id === 1 || allocationType.id === 4)
        ) {
          renderOtherInfo = otherInfo || "";
        } else if (
          agencyWithSpecialForms.includes(Number(selectedAgency?.ID)) &&
          (allocationType.id === 1 || allocationType.id === 4)
        ) {
          renderOtherInfo = institution?.name;
        } else if (allocationType.id === 2) {
          renderOtherInfo = allocationName;
        } else if (allocationType.id === 3) {
          renderOtherInfo = allocationContactName;
        }

        return renderOtherInfo ? (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {renderOtherInfo}
          </div>
        ) : (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {"-"}
          </div>
        );
      },
    },
    {
      name: "Status",
      selector: (row) => row?.status,
      identifier: "status",
      grow: 2,
      sortable: true,
      minWidth: "180px",
      cell: ({ status }, index) => (
        <StyledCustomSelect
          className={`${
            isPendingOrRequested(status) ? "font-weight-bold" : ""
          } ${cannotEditStatus(status, role) ? "" : "pointer"}`}
          isSearchable={false}
          isDisabled={cannotEditStatus(status, role)}
          onChange={(option) => {
            handleStatusChange(index, option);
          }}
          value={ALLOCATION_STATUS_OPTIONS.find(
            (stat) => stat?.value === status
          )}
          options={getStatusOptions(status, role)}
          styles={statusSelectStyles}
          getOptionLabel={(option) => {
            return option?.displayLabel;
          }}
          menuPortalTarget={document.body}
        />
      ),
    },
    {
      name: "Recommended Implementing Agency",
      selector: (row) => row?.implementing,
      identifier: "implementing",
      grow: 2,
      sortable: false,
      minWidth: "165px",
      cell: (row, index) => (
        <StyledCustomSelect
          className={`${
            isPendingOrRequested(row.status) ? "font-weight-bold" : ""
          }`}
          isSearchable
          isDisabled={hasPermission("recipient-update")}
          onChange={(option) => {
            const newData = [...tableOption.data];

            newData[index] = {
              ...row,
              implementing: {
                ...newData[index].implementing,
                id: option?.value,
              },
            };

            setTableOption({
              ...tableOption,
              data: newData,
            });
          }}
          value={regions.find(
            (region) => region.value === row.implementing?.id
          )}
          options={regions}
          styles={regionSelectStyles}
          menuPortalTarget={document.body}
          maxMenuHeight="150px"
        />
      ),
    },
    {
      name: "Province",
      selector: (row) => row?.province,
      identifier: "province",
      id: "province",
      sortable: true,
      minWidth: "170px",
      cell: ({ status, province }) =>
        province ? (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {province?.name}
          </div>
        ) : (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {"-"}
          </div>
        ),
    },
    {
      name: "Municipality",
      selector: (row) => row?.municipality,
      identifier: "municipality",
      id: "municipality",
      sortable: true,
      minWidth: "170px",
      cell: ({ status, municipality }) =>
        municipality ? (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {municipality?.name}
          </div>
        ) : (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {"-"}
          </div>
        ),
    },
    {
      name: "Date Requested",
      selector: (row) => row?.dateRequested,
      identifier: "dateRequested",
      sortable: true,
      minWidth: "150px",
      cell: ({ status, dateRequested }) => (
        <div
          className={`${
            isPendingOrRequested(status) ? "font-weight-bold" : ""
          }`}
        >
          {getFormattedDateString(dateRequested, "MMM-dd-yyyy")}
        </div>
      ),
    },
    {
      name: "Date Endorsed",
      selector: (row) => row?.dateApproved,
      identifier: "dateApproved",
      sortable: true,
      minWidth: "150px",
      cell: ({ dateApproved, status }) =>
        dateApproved ? (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {getFormattedDateString(dateApproved, "MMM-dd-yyyy")}
          </div>
        ) : (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {"-"}
          </div>
        ),
    },
    {
      name: "Remarks",
      selector: (row) => row?.hasComments,
      sortable: false,
      minWidth: "160px",
      cell: (row) => (
        <CommentsContainer>
          {row.hasComments === 0 && hasPermission("comment-create") && (
            <AddCommentBtn
              size="sm"
              onClick={() => {
                setEditingCongressman(row.id);
                fetchRecipientMsgs(row.id);
                toggleModal();
              }}
            >
              {"Add Comment"}
            </AddCommentBtn>
          )}

          {row.hasComments === 1 && hasPermission("comment-view") && (
            <ViewCommentBtn
              color="primary"
              size="sm"
              onClick={() => {
                setEditingCongressman(row.id);
                fetchRecipientMsgs(row.id);
                toggleModal();
              }}
            >
              {"View Comment"}
            </ViewCommentBtn>
          )}
        </CommentsContainer>
      ),
    },
    {
      name: "Updated By",
      selector: (row) => row?.updatedBy,
      identifier: "updatedBy",
      grow: 1,
      sortable: true,
      minWidth: "150px",
      cell: ({ status, updatedBy }) =>
        updatedBy && updatedBy !== null ? (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {updatedBy}
          </div>
        ) : (
          <div
            className={`${
              isPendingOrRequested(status) ? "font-weight-bold" : ""
            }`}
          >
            {"-"}
          </div>
        ),
    },
    {
      name: "Actions",
      selector: (row) => row?.id,
      sortable: false,
      width: "150px",
      center: true,
      cell: (row, index) => {
        const isBumpIconDisabled =
          timer && timer?.find((time) => time?.id === row.id && time?.isActive);

        return (
          <>
            {canEditRow(row?.status, role) && (
              <>
                <StyledEditIcon
                  size={16}
                  id={`edit-row--${index}`}
                  onClick={() => {
                    navigate(
                      `/representatives/${id}/cong/${congID}/recipient`,
                      {
                        state: {
                          row,
                          agency: selectedAgency?.ID,
                          program: congDetails?.Projects.find(
                            (project) => project.name === row?.project
                          )?.id,
                          action: EDIT_ACTION,
                        },
                      }
                    );
                  }}
                />
                <UncontrolledTooltip
                  placement="left"
                  target={`edit-row--${index}`}
                >
                  {"Edit"}
                </UncontrolledTooltip>
              </>
            )}

            <StyledEyeIcon
              size={16}
              onClick={() =>
                navigate(`/representatives/${id}/cong/${congID}/recipient`, {
                  state: {
                    row,
                    agency: selectedAgency?.ID,
                    program: congDetails?.Projects.find(
                      (project) => project.name === row?.project
                    )?.id,
                    action: VIEW_ACTION,
                  },
                })
              }
              id={`view-row--${index}`}
            />
            <UncontrolledTooltip
              placement="top"
              target={`view-row--${index}`}
            >
              {"View"}
            </UncontrolledTooltip>

            {ALLOWED_STATUS_TO_BUMP_ALLOCATION.includes(row.status) && (
              <>
                <StyledFontAwesomeIcon
                  id={`bump-row--${index}`}
                  icon={faCommentDollar}
                  size="xl"
                  style={
                    !isBumpIconDisabled
                      ? { cursor: "pointer" }
                      : { cursor: "initial", pointerEvents: "none" }
                  }
                  disabled={isBumpIconDisabled}
                  onClick={() => handleBumpIconOnClicked(row.id, row.status)}
                />

                <UncontrolledTooltip
                  placement="top"
                  target={`bump-row--${index}`}
                >
                  {"Bump"}
                </UncontrolledTooltip>
              </>
            )}

            {row.status === ALLOCATION_STATUS.REQUESTED &&
              isRoleCongressman && (
                <>
                  <StyledTrashIcon
                    size={16}
                    id={`delete-row--${index}`}
                    onClick={() => handleDeleteAction(row)}
                  />
                  <UncontrolledTooltip
                    placement="top"
                    target={`delete-row--${index}`}
                  >
                    {"Delete"}
                  </UncontrolledTooltip>
                </>
              )}
          </>
        );
      },
    },
  ];

  let newColumns = [...columns];

  if (selectedAgency?.ID === 14 && role && canEditRoles.includes(role)) {
    columns.splice(10, 0, fundSourceColumn);
  }

  if (role && canEditRoles.includes(role)) {
    columns.splice(12, 0, editorNotesColumn);
  }

  // Add Name column for speaker-view in Others tab
  if (isSpeakerViewOthersTab(activeEndorsementTab)) {
    // Insert Name column after ID column (index 2)
    columns.splice(3, 0, nameColumn);
  }

  if (congRoles.includes(role)) {
    newColumns = columns.filter((col) => col.identifier !== "updatedBy");
  }

  return newColumns;
};
